import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, RequireAuth, RequireSuperadmin } from "@/contexts/AuthContext";
import { BootScreen } from "@/components/flow/BootScreen";
import { ConsentBanner } from "@/components/flow/ConsentBanner";
import { PageMeta } from "@/lib/seo";
import HomePage from "@/pages/HomePage";

/* ---------------------------------------------------------------------------
   Route-level code splitting. The marketing page ships in the main bundle;
   everything else (portal, HQ, owner surfaces, pricing) loads on demand so the
   landing page stays light and fast.
--------------------------------------------------------------------------- */
const AdminShell = lazy(() => import("@/components/flow/AdminShell").then((m) => ({ default: m.AdminShell })));
const HqShell = lazy(() => import("@/components/flow/HqShell").then((m) => ({ default: m.HqShell })));
const HqOverviewPage = lazy(() => import("@/pages/hq/HqOverviewPage"));
const HqOrganizationsPage = lazy(() => import("@/pages/hq/HqOrganizationsPage"));
const HqIntegrationsPage = lazy(() => import("@/pages/hq/HqIntegrationsPage"));
const HqSettingsPage = lazy(() => import("@/pages/hq/HqSettingsPage"));
const HqGrowthPage = lazy(() => import("@/pages/hq/HqGrowthPage"));
const HqSupportPage = lazy(() => import("@/pages/hq/HqSupportPage"));
const HqBillingPage = lazy(() => import("@/pages/hq/HqBillingPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const EarlyAccessPage = lazy(() => import("@/pages/EarlyAccessPage"));
const StartPage = lazy(() => import("@/pages/public/StartPage"));
const WidgetPage = lazy(() => import("@/pages/public/WidgetPage"));
const OfferPage = lazy(() => import("@/pages/owner/OfferPage"));
const SignPage = lazy(() => import("@/pages/owner/SignPage"));
const OnboardingPage = lazy(() => import("@/pages/owner/OnboardingPage"));
const LoginPage = lazy(() => import("@/pages/admin/LoginPage"));
const DashboardPage = lazy(() => import("@/pages/admin/DashboardPage"));
const PipelinePage = lazy(() => import("@/pages/admin/PipelinePage"));
const OutboxPage = lazy(() => import("@/pages/admin/OutboxPage"));
const InboxPage = lazy(() => import("@/pages/admin/InboxPage"));
const OwnersPage = lazy(() => import("@/pages/admin/OwnersPage"));
const OwnerDetailPage = lazy(() => import("@/pages/admin/OwnerDetailPage"));
const PropertiesPage = lazy(() => import("@/pages/admin/PropertiesPage"));
const LeadDetailPage = lazy(() => import("@/pages/admin/LeadDetailPage"));
const OfferBuilderPage = lazy(() => import("@/pages/admin/OfferBuilderPage"));
const TemplateDesignerPage = lazy(() => import("@/pages/admin/TemplateDesignerPage"));
const GifPage = lazy(() => import("@/pages/gif/GifPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/admin/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/admin/ResetPasswordPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const HelpPage = lazy(() => import("@/pages/admin/HelpPage"));
const SetupPage = lazy(() => import("@/pages/admin/SetupPage"));
const ContractPreviewPage = lazy(() => import("@/pages/admin/PreviewPage").then((m) => ({ default: m.ContractPreviewPage })));
const OfferTemplatePreviewPage = lazy(() => import("@/pages/admin/PreviewPage").then((m) => ({ default: m.OfferTemplatePreviewPage })));

/** Redirect that carries the query string and hash along (campaign attribution, deep links). */
function RedirectKeepingQuery({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/** Quiet full-screen fallback while a route chunk loads — the same canvas the page paints on,
    plus a 2px brand line if it takes longer than a beat. No skeleton of a layout that may not match. */
function RouteFallback() {
  return <BootScreen data-testid="route-loading" />;
}

/** Every route change lands at the top of the new page (hash links like /#journey keep their target;
    same-page marketing morphs — /  ⇄ /for/{domain} via replaceState — never pass through here). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageMeta />
      <ConsentBanner />
      <AuthProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Marketing */}
            <Route path="/" element={<HomePage />} />
            {/* Personalised marketing pages: a shared preview link, or a page made for one company's domain */}
            <Route path="/preview/:token" element={<HomePage />} />
            <Route path="/for/:domain" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/early-access" element={<EarlyAccessPage />} />
            {/* Outreach animation (GIF/MP4 source) */}
            <Route path="/gif" element={<GifPage />} />
            {/* Legacy campaign links keep working: /waitlist?c=… → /early-access?c=… */}
            <Route path="/waitlist" element={<RedirectKeepingQuery to="/early-access" />} />

            {/* Public lead capture (tenant-branded) */}
            <Route path="/start/:slug" element={<StartPage />} />
            <Route path="/w/:slug" element={<WidgetPage />} />

            {/* Owner surfaces (tokenized, no account) */}
            <Route path="/offer/:token" element={<OfferPage />} />
            <Route path="/sign/:token" element={<SignPage />} />
            <Route path="/onboard/:token" element={<OnboardingPage />} />

            {/* Admin */}
            <Route path="/app/login" element={<LoginPage mode="login" />} />
            <Route path="/app/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/app/reset-password" element={<ResetPasswordPage />} />
            <Route path="/app/register" element={<LoginPage mode="register" />} />
            {/* Template previews — full-tab, signed-in, outside the admin shell (what the owner will see) */}
            <Route path="/app/preview/contract" element={<RequireAuth><ContractPreviewPage /></RequireAuth>} />
            <Route path="/app/preview/offer-template/:templateId" element={<RequireAuth><OfferTemplatePreviewPage /></RequireAuth>} />
            {/* "Kom i gang" — first-run setup wizard; full-page, outside the admin shell */}
            <Route path="/app/setup" element={<RequireAuth><SetupPage /></RequireAuth>} />
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <AdminShell />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="pipeline" element={<PipelinePage />} />
              <Route path="leads/:leadId" element={<LeadDetailPage />} />
              <Route path="owners" element={<OwnersPage />} />
              <Route path="owners/:ownerId" element={<OwnerDetailPage />} />
              <Route path="properties" element={<PropertiesPage />} />
              <Route path="offers/:offerId" element={<OfferBuilderPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="outbox" element={<OutboxPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/templates/:templateId" element={<TemplateDesignerPage />} />
              <Route path="settings/:section" element={<SettingsPage />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="help/:id" element={<HelpPage />} />
            </Route>

            {/* Flow HQ — platform superadmin */}
            <Route
              path="/hq"
              element={
                <RequireSuperadmin>
                  <HqShell />
                </RequireSuperadmin>
              }
            >
              <Route index element={<HqOverviewPage />} />
              <Route path="organizations" element={<HqOrganizationsPage />} />
              <Route path="growth" element={<HqGrowthPage />} />
              <Route path="support" element={<HqSupportPage />} />
              <Route path="billing" element={<HqBillingPage />} />
              <Route path="integrations" element={<HqIntegrationsPage />} />
              <Route path="settings" element={<HqSettingsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors={false} closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
